import { CHART_SERIES_PALETTE } from "@/shared/ui/chart/public";

/**
 * Data Dashboard 차트의 색·마크 규약. 렌더러에 의존하지 않고 CSS 변수 문자열만 다룬다.
 *
 * 값을 TS 리터럴로 두지 않는 이유는 팔레트 파일과 같다. 테마 전환이 리렌더링 없이 CSS 로만
 * 끝나야 차트 fill 과 범례 스와치가 같은 프레임에 함께 바뀐다.
 */

/**
 * 크기 비교 막대의 단일 hue. 값마다 색을 바꾸지 않는다. 길이가 이미 크기를 표현한다. 순위에
 * 색을 연결하면 데이터가 바뀔 때 같은 값의 색이 바뀐다.
 */
export const MAGNITUDE_FILL = "var(--app-chart-magnitude)";

/** 같은 hue 의 옅은 단계. 분위 스트립의 수염처럼 덜 강조할 마크가 쓴다. */
export const MAGNITUDE_SOFT_FILL = "var(--app-chart-magnitude-soft)";

/**
 * 미판정·미지정 묶음의 중립색. 모든 축에서 같다.
 *
 * 본문 텍스트용 muted 토큰을 쓰지 않는다. 그 토큰은 다크에서 밝은 회색이 되어 데이터 마크보다
 * 더 눈에 띈다. 이 단계는 배경 대비 3:1 을 넘지만 주 마크보다 분명히 덜 강조된다.
 */
export const UNKNOWN_FILL = "var(--app-chart-unknown)";

/**
 * 부분-전체 축의 categorical 슬롯. dataviz 검증기로 라이트·다크 전 항목을 통과한 비교 팔레트를
 * 그대로 쓴다. 그래서 두 화면이 같은 색 어휘를 가진다.
 *
 * 슬롯 번호는 배열 인덱스(=count 순위)가 아니라 **값의 고정 vocab 순서**로 정한다.
 * 색이 값을 따라야 데이터가 바뀌어도 같은 값이 같은 색으로 남는다.
 */
export function shareSlotColor(slot: number): string {
  return CHART_SERIES_PALETTE[slot % CHART_SERIES_PALETTE.length];
}

/**
 * 히트맵 순차 램프. magnitude 와 같은 파랑 hue 5단계다. 값은 styles.css 테마 블록이 소유한다.
 * 순차 스케일이라 categorical 팔레트를 쓰지 않는다(크기에 따라 hue 를 바꾸면 순서가 사라진다).
 */
export const HEAT_RAMP: readonly string[] = [
  "var(--app-chart-heat-1)",
  "var(--app-chart-heat-2)",
  "var(--app-chart-heat-3)",
  "var(--app-chart-heat-4)",
  "var(--app-chart-heat-5)",
];

/** intensity(0..1] → 램프 단계(1..5). 0 은 빈 배경(칠하지 않음)이라 이 함수로 오지 않는다. */
export function heatStepOf(intensity: number): number {
  return Math.min(HEAT_RAMP.length, Math.max(1, Math.ceil(intensity * HEAT_RAMP.length)));
}

export function heatFill(intensity: number): string {
  return HEAT_RAMP[heatStepOf(intensity) - 1];
}

/**
 * 이 단계부터 셀 잉크를 반전한다(`--app-chart-heat-ink-strong`). 두 테마 모두 3단계부터
 * 기본 텍스트 잉크의 대비(4.5:1)를 만족하지 못한다. 근거 수치는 styles.css 램프 주석에 있다.
 */
export const HEAT_INK_STRONG_STEP = 3;

/** 마크 기하. dataviz 규약을 따른다(얇은 마크, 데이터 끝 4px 라운드, 세그먼트 사이 2px 배경 간격). */
export const MARK = {
  /** 막대 끝 라운드. 차트 축에 붙은 쪽은 각지게 둔다. */
  radius: 4,
  /** 누적 세그먼트 사이와 인접 막대 사이의 배경 간격. */
  gap: 2,
  /** 선 굵기. */
  strokeWidth: 2,
  /** 차트 눈금 글자 크기. */
  tickFontSize: 11,
} as const;
